import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import "./App.css";
import TeamIconSelector from "./TeamIconSelector";
import config from "./config";
import { validateAllowedText } from "./utils/contentModeration";
import { devLog } from "./utils/devLog";
import teamManagementApi from "./services/teamManagementApi";
import seasonApi from "./services/seasonApi";
import tradeApi from "./services/tradeApi";
import waiversApi from "./services/waiversApi";
import { getLockCountdown, getLockStatusMessage } from "./utils/lockUtils";
import { getLineupAndTransactionInfoCopy, resolveUnlockSettings } from "./utils/transactionWindowCopy";
import { getStarterSlotDefinitions, starterSlotRowStyle } from "./utils/starterSlots";
import { findTeamByNameInList } from "./utils/leagueTeamLookup";
import { teamSlotsMatch } from "./utils/tradeTeamUtils";
import { resolveDefaultFantasyWeek } from "./utils/seasonCalendar";

// Mock data for demonstration
const mockRoster = [
  { player_id: "qb1", player_name: "Patrick Mahomes", position: "QB" },
  { player_id: "qb2", player_name: "Josh Allen", position: "QB" },
  { player_id: "qb3", player_name: "Jalen Hurts", position: "QB" },
  { player_id: "rb1", player_name: "Christian McCaffrey", position: "RB" },
  { player_id: "rb2", player_name: "Austin Ekeler", position: "RB" },
  { player_id: "rb3", player_name: "Bijan Robinson", position: "RB" },
  { player_id: "wr1", player_name: "Justin Jefferson", position: "WR" },
  { player_id: "wr2", player_name: "Tyreek Hill", position: "WR" },
  { player_id: "wr3", player_name: "Amon-Ra St. Brown", position: "WR" },
  { player_id: "te1", player_name: "Travis Kelce", position: "TE" },
  { player_id: "te2", player_name: "Mark Andrews", position: "TE" },
  { player_id: "te3", player_name: "Sam LaPorta", position: "TE" },
];

function mergeWeekLineup(leagueSettings, lineupData, benchCount) {
  const defs = getStarterSlotDefinitions(leagueSettings || {});
  const defaults = {};
  for (const s of defs) {
    defaults[s.key] = { player_id: "" };
  }
  const src = lineupData && typeof lineupData === "object" ? lineupData : {};
  const merged = { ...defaults };
  for (const s of defs) {
    const v = src[s.key];
    if (v && typeof v === "object") {
      merged[s.key] = {
        player_id: v.player_id != null ? String(v.player_id) : "",
      };
    }
  }
  merged.BENCH = Array.isArray(src.BENCH) ? [...src.BENCH] : Array(benchCount).fill("");
  while (merged.BENCH.length < benchCount) merged.BENCH.push("");
  return merged;
}

function lineupWeekSnapshot(weekLineup, leagueSettings, benchCount) {
  return JSON.stringify(mergeWeekLineup(leagueSettings, weekLineup, benchCount));
}

const BENCH_COUNT = 5;
const totalWeeks = 17;

export default function TeamManagement({ onBack, leagueId, userRole, currentUser, teamName: propTeamName, leagueSettings, showToast, refreshLeagueSettings = null, navigate }) {
  const getDefaultLineup = useCallback(() => {
    // { week: { slotKey: { player_id, time } } }
    const lineup = {};
    for (let w = 1; w <= totalWeeks; w++) {
      lineup[w] = {};
      for (const slot of getStarterSlotDefinitions(leagueSettings || {})) {
        lineup[w][slot.key] = { player_id: "" };
      }
      lineup[w].BENCH = Array(BENCH_COUNT).fill("");
    }
    return lineup;
  }, [leagueSettings]);

  // Get team count from leagueSettings, fallback to 10 if not available
  const teamCount = leagueSettings?.teamCount || 10;
  const [searchParams] = useSearchParams();
  const teamFromUrl = searchParams.get("team");

  const resolveInitialTeam = () => {
    if (teamFromUrl && /^Team\d+$/i.test(teamFromUrl)) {
      const num = parseInt(teamFromUrl.replace(/team/i, ""), 10);
      if (num >= 1 && num <= teamCount) return `Team${num}`;
    }
    if (propTeamName && /^Team\d+$/i.test(propTeamName)) return propTeamName;
    return "Team1";
  };

  const [week, setWeek] = useState(1);
  const [calendarWeek, setCalendarWeek] = useState(1);
  const [selectedTeam, setSelectedTeam] = useState(resolveInitialTeam);
  const [lineup, setLineup] = useState(getDefaultLineup());
  const [roster, setRoster] = useState(mockRoster);
  const [saveMsg, setSaveMsg] = useState("");
  const [incomingTradeCount, setIncomingTradeCount] = useState(0);
  const [dropPlayerId, setDropPlayerId] = useState("");
  const [dropMsg, setDropMsg] = useState("");
  const [lockStatus, setLockStatus] = useState({ isLocked: false });
  const [teamName, setTeamName] = useState("");
  const [teamNames, setTeamNames] = useState({});
  const [processedWeeks, setProcessedWeeks] = useState([]);
  const [savedLineupSnapshot, setSavedLineupSnapshot] = useState("");
  const [copyingLastWeek, setCopyingLastWeek] = useState(false);
  const [userTeamInfo, setUserTeamInfo] = useState(null);
  const hasAppliedUserTeamDefault = useRef(false);

  // Open the team from ?team=TeamN when linked from standings or elsewhere
  useEffect(() => {
    if (!teamFromUrl || !/^Team\d+$/i.test(teamFromUrl)) return;
    const num = parseInt(teamFromUrl.replace(/team/i, ""), 10);
    if (num >= 1 && num <= teamCount) {
      setSelectedTeam(`Team${num}`);
    }
  }, [teamFromUrl, teamCount]);

  const isCommissioner = userRole === 'commissioner';
  const isMyTeam = Boolean(
    userTeamInfo?.user_team && selectedTeam && userTeamInfo.user_team === selectedTeam
  );
  const canEdit = isMyTeam || isCommissioner;
  const weekLineupLocked = processedWeeks.includes(week);
  const unlockDay = leagueSettings?.rosterUnlockDay ?? "Wednesday";
  const unlockHour = leagueSettings?.rosterUnlockHour ?? 5;

  useEffect(() => {
    const tick = () => setLockStatus(getLockCountdown(unlockDay, unlockHour));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [unlockDay, unlockHour]);

  const handleDropToWaivers = async () => {
    if (!dropPlayerId || !canEdit) return;
    const player = roster.find((p) => String(p.player_id) === String(dropPlayerId));
    if (!player) return;
    if (!window.confirm(`Drop ${player.player_name} to free agency?`)) return;
    try {
      const data = await waiversApi.dropPlayerToFreeAgency(leagueId, selectedTeam, player, currentUser);
      setRoster(Array.isArray(data.roster_data) ? data.roster_data : roster.filter((p) => String(p.player_id) !== String(dropPlayerId)));
      setDropPlayerId("");
      setDropMsg(`✅ Dropped ${player.player_name} to free agency.`);
      setTimeout(() => setDropMsg(""), 3500);
    } catch (error) {
      setDropMsg(`❌ ${error.message || "Failed to drop player"}`);
      setTimeout(() => setDropMsg(""), 4500);
    }
  };

  useEffect(() => {
    let cancelled = false;
    resolveDefaultFantasyWeek().then((w) => {
      if (!cancelled) {
        setCalendarWeek(w);
        setWeek(w);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [leagueId]);
  
  // Load lineup for selected team/week from API
  useEffect(() => {
    const loadLineup = async () => {
      try {
        const lineupData = await teamManagementApi.getLineup(leagueId, selectedTeam, week, currentUser);
        const merged = mergeWeekLineup(leagueSettings, lineupData, BENCH_COUNT);
        setLineup(prev => ({
          ...prev,
          [week]: merged,
        }));
        setSavedLineupSnapshot(lineupWeekSnapshot(merged, leagueSettings, BENCH_COUNT));
      } catch (error) {
        console.error('Error loading lineup:', error);
        const empty = mergeWeekLineup(leagueSettings, null, BENCH_COUNT);
        setLineup(prev => ({
          ...prev,
          [week]: empty,
        }));
        setSavedLineupSnapshot(lineupWeekSnapshot(empty, leagueSettings, BENCH_COUNT));
      }
    };
    
    loadLineup();
  }, [selectedTeam, week, leagueId, getDefaultLineup, currentUser, leagueSettings]);

  // Load roster from TeamRoster JSON (source of truth after trades), then fall back to /teams
  useEffect(() => {
    const fetchTeamRoster = async () => {
      try {
        devLog(`🔄 Fetching roster for team: "${selectedTeam}"`);
        const rosterRows = await tradeApi.getTeamRoster(selectedTeam, leagueId);
        if (rosterRows?.length) {
          const rosterData = rosterRows.map((player) => ({
            player_id: String(player.player_id || player.id || ""),
            player_name: player.player_name || player.name || "Unknown",
            position: player.position,
            avg_fantasy_points: player.avg_fantasy_points || 0,
            salary: player.salary || 0,
          }));
          setRoster(rosterData);
          return;
        }

        const response = await fetch(`${config.API_URL}/leagues/${leagueId}/teams`, {
          credentials: "include",
        });
        if (!response.ok) {
          setRoster([]);
          return;
        }
        const data = await response.json();
        const team = findTeamByNameInList(data.teams, selectedTeam);
        if (team?.roster?.length) {
          setRoster(
            team.roster.map((player) => ({
              player_id: String(player.player_id || player.id),
              player_name: player.player_name,
              position: player.position,
              avg_fantasy_points: 0,
              salary: 0,
            }))
          );
        } else {
          setRoster([]);
        }
      } catch (error) {
        console.error("Error fetching team roster:", error);
        setRoster([]);
      }
    };

    fetchTeamRoster();
  }, [selectedTeam, leagueId]);

  // Pending trades addressed to the user's team
  useEffect(() => {
    const loadIncomingTrades = async () => {
      if (!leagueId || !userTeamInfo?.user_team) {
        setIncomingTradeCount(0);
        return;
      }
      try {
        const trades = await tradeApi.getTrades(leagueId);
        const count = trades.filter(
          (t) =>
            t.status === "pending" &&
            teamSlotsMatch(t.to, userTeamInfo.user_team)
        ).length;
        setIncomingTradeCount(count);
      } catch (error) {
        console.error("Error loading incoming trades:", error);
        setIncomingTradeCount(0);
      }
    };
    loadIncomingTrades();
  }, [leagueId, userTeamInfo?.user_team]);

  useEffect(() => {
    let cancelled = false;
    async function loadProcessedWeeks() {
      if (!leagueId || !currentUser?.id) {
        if (!cancelled) setProcessedWeeks([]);
        return;
      }
      try {
        const weeks = await seasonApi.getProcessedWeekNumbers(leagueId, currentUser);
        if (!cancelled) setProcessedWeeks(weeks);
      } catch {
        if (!cancelled) setProcessedWeeks([]);
      }
    }
    loadProcessedWeeks();
    return () => {
      cancelled = true;
    };
  }, [leagueId, currentUser, week]);

  // Load team name for selected team from API
  useEffect(() => {
    const loadTeamName = async () => {
      try {
        const teamDisplayName = await teamManagementApi.getTeamName(leagueId, selectedTeam, currentUser);
        setTeamName(teamDisplayName);
      } catch (error) {
        console.error('Error loading team name:', error);
        setTeamName(selectedTeam.replace("Team", "Team "));
      }
    };
    
    loadTeamName();
  }, [selectedTeam, leagueId, currentUser]);

  // Load all team names for dropdown
  useEffect(() => {
    const loadAllTeamNames = async () => {
      try {
        const customizations = await teamManagementApi.getTeamCustomizations(leagueId, currentUser);
        const names = {};
        for (let i = 1; i <= teamCount; i++) {
          const teamKey = `Team${i}`;
          const customization = customizations.find(c => c.team_name === teamKey);
          names[teamKey] = customization?.display_name || teamKey.replace("Team", "Team ");
        }
        setTeamNames(names);
      } catch (error) {
        console.error('Error loading team names:', error);
        // Fallback to default names
        const names = {};
        for (let i = 1; i <= teamCount; i++) {
          const teamKey = `Team${i}`;
          names[teamKey] = teamKey.replace("Team", "Team ");
        }
        setTeamNames(names);
      }
    };
    
    loadAllTeamNames();
  }, [leagueId, currentUser, teamCount]);

  // Load user's team information
  useEffect(() => {
    const loadUserTeamInfo = async () => {
      try {
        const response = await fetch(`${config.API_URL}/leagues/${leagueId}/user-team?user_id=${currentUser?.id}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setUserTeamInfo(data);
        }
      } catch (error) {
        console.error('Error loading user team info:', error);
      }
    };
    
    if (currentUser) {
      loadUserTeamInfo();
    }
  }, [leagueId, teamCount, currentUser]);

  // Default to the user's assigned slot when opening Team Management (unless URL specifies a team)
  useEffect(() => {
    if (hasAppliedUserTeamDefault.current || teamFromUrl) return;
    if (userTeamInfo?.user_team) {
      setSelectedTeam(userTeamInfo.user_team);
      hasAppliedUserTeamDefault.current = true;
    }
  }, [userTeamInfo, teamFromUrl]);

  // Get assigned player_ids for this week (starters + bench)
  const assignedIds = new Set();
  for (const slot of getStarterSlotDefinitions(leagueSettings || {})) {
    const pid = lineup[week][slot.key]?.player_id;
    if (pid) assignedIds.add(pid);
  }
  for (const pid of lineup[week].BENCH) {
    if (pid) assignedIds.add(pid);
  }

  // Handle starter assignment
  const handleAssignStarter = (slotKey, player_id) => {
    setLineup((prev) => ({
      ...prev,
      [week]: {
        ...prev[week],
        [slotKey]: { ...prev[week][slotKey], player_id },
      },
    }));
  };
  const [revealSchedule, setRevealSchedule] = useState({});

  const loadRevealSchedule = useCallback(async () => {
    if (!leagueId || !selectedTeam || !currentUser?.id) {
      setRevealSchedule({});
      return;
    }
    try {
      const rows = await seasonApi.getRandomPlayerPointsForWeek(
        leagueId,
        selectedTeam,
        week,
        currentUser
      );
      const map = {};
      for (const row of rows) {
        map[row.slot] = {
          assigned: Boolean(row.assigned),
          revealed: row.revealed,
          reveal_at_label: row.reveal_at_label,
        };
      }
      setRevealSchedule(map);
    } catch {
      setRevealSchedule({});
    }
  }, [leagueId, selectedTeam, week, currentUser]);

  useEffect(() => {
    loadRevealSchedule();
  }, [loadRevealSchedule]);

  const isStarterSlotEditable = (slotKey) => {
    if (!canEdit || weekLineupLocked) return false;
    return !revealSchedule[slotKey]?.revealed;
  };

  // Get available options for a slot (not already assigned to another starter slot this week)
  const getOptions = (eligible, currentId) => {
    // Only exclude players assigned to other starter slots (not bench)
    const assignedStarterIds = new Set();
    for (const slot of getStarterSlotDefinitions(leagueSettings || {})) {
      const pid = lineup[week][slot.key]?.player_id;
      if (pid && pid !== currentId) assignedStarterIds.add(pid);
    }
    return roster.filter(
      (p) => eligible.includes(p.position) && (!assignedStarterIds.has(p.player_id) || p.player_id === currentId)
    );
  };

  // Save lineup for selected team/week
  const handleSaveLineup = async () => {
    if (weekLineupLocked) {
      setSaveMsg("❌ Week scoring is final — lineups can no longer be changed.");
      showToast && showToast("Week scoring is final — lineups can no longer be changed.", "error");
      setTimeout(() => setSaveMsg(""), 3000);
      return;
    }
    
    try {
      await teamManagementApi.saveLineup(leagueId, selectedTeam, week, lineup[week], currentUser);
      await loadRevealSchedule();
      setSavedLineupSnapshot(lineupWeekSnapshot(lineup[week], leagueSettings, BENCH_COUNT));
      setSaveMsg("✅ Lineup saved!");
      showToast && showToast("Lineup saved!", "success");
      setTimeout(() => setSaveMsg(""), 2000);
    } catch (error) {
      console.error('Error saving lineup:', error);
      setSaveMsg("Error saving lineup");
      showToast && showToast("Error saving lineup", "error");
      setTimeout(() => setSaveMsg(""), 2000);
    }
  };

  const handleCopyLastWeek = async () => {
    if (week <= 1 || weekLineupLocked || !canEdit || copyingLastWeek) return;
    setCopyingLastWeek(true);
    try {
      const prevLineupData = await teamManagementApi.getLineup(
        leagueId,
        selectedTeam,
        week - 1,
        currentUser
      );
      const merged = mergeWeekLineup(leagueSettings, prevLineupData, BENCH_COUNT);
      setLineup((prev) => ({ ...prev, [week]: merged }));
      showToast &&
        showToast(
          `Copied Week ${week - 1} lineup — review and save when ready.`,
          "success"
        );
    } catch (error) {
      console.error("Error copying last week lineup:", error);
      showToast && showToast("Could not copy last week's lineup.", "error");
    } finally {
      setCopyingLastWeek(false);
    }
  };

  const currentWeekLineup =
    lineup[week] ?? mergeWeekLineup(leagueSettings, null, BENCH_COUNT);
  const lineupHasUnsavedChanges =
    canEdit &&
    !weekLineupLocked &&
    savedLineupSnapshot !== "" &&
    lineupWeekSnapshot(currentWeekLineup, leagueSettings, BENCH_COUNT) !== savedLineupSnapshot;

  // Auto-set roster for the selected week
  const handleAutoSetRoster = () => {
    if (weekLineupLocked) {
      setSaveMsg("❌ Week scoring is final — lineups can no longer be changed.");
      showToast && showToast("Week scoring is final — lineups can no longer be changed.", "error");
      setTimeout(() => setSaveMsg(""), 3000);
      return;
    }
    
    // Build a new lineup for this week
    const used = new Set();
    const posCounts = { QB: 0, RB: 0, WR: 0, TE: 0, K: 0 };
    const newWeekLineup = {};
    // Helper to get next eligible player for a position, not already used, and not exceeding 4 per position
    const getNext = (pos) => roster.find((p) => p.position === pos && !used.has(p.player_id) && posCounts[pos] < 4);

    // Prepare timeslot distribution for all starters
    const starterSlots = getStarterSlotDefinitions(leagueSettings || {});

    // Fill each starter slot
    for (const slot of starterSlots) {
      let player = null;
      if (slot.key.startsWith("RB")) player = getNext("RB");
      else if (slot.key.startsWith("WR")) player = getNext("WR");
      else if (slot.key.startsWith("K")) player = getNext("K");
      else if (slot.key === "QB") player = getNext("QB");
      else if (slot.key === "TE") player = getNext("TE");
      else if (slot.key === "FLEX" || slot.key === "FLEX_SILVER" || slot.key === "FLEX_GOLD")
        player = roster.find((p) => ["RB", "WR", "TE"].includes(p.position) && !used.has(p.player_id) && posCounts[p.position] < 4);
      newWeekLineup[slot.key] = {
        player_id: player ? String(player.player_id) : "",
      };
      if (player) { used.add(player.player_id); posCounts[player.position] = (posCounts[player.position] || 0) + 1; }
    }
    // Fill bench with any remaining players, but never exceed 4 of any position (starters + bench)
    const bench = [];
    for (const p of roster) {
      if (!used.has(p.player_id) && posCounts[p.position] < 4 && bench.length < BENCH_COUNT) {
        bench.push(String(p.player_id));
        used.add(p.player_id);
        posCounts[p.position]++;
      }
      if (bench.length >= BENCH_COUNT) break;
    }
    newWeekLineup.BENCH = bench;
    // Update local state only here — do not call the API inside the setState updater.
    // React 18 Strict Mode (dev) double-invokes functional updaters, which would POST twice and
    // hit the DB unique constraint (league_id, team_name, week) on the second INSERT → 500.
    setLineup((prev) => ({ ...prev, [week]: newWeekLineup }));
    teamManagementApi.saveLineup(leagueId, selectedTeam, week, newWeekLineup, currentUser)
      .then(() => loadRevealSchedule())
      .then(() => {
        setSavedLineupSnapshot(lineupWeekSnapshot(newWeekLineup, leagueSettings, BENCH_COUNT));
        setSaveMsg("✅ Lineup auto-set and saved!");
        showToast && showToast("Lineup auto-set and saved!", "success");
        setTimeout(() => setSaveMsg(""), 2000);
      })
      .catch((error) => {
        console.error('Error auto-setting lineup:', error);
        setSaveMsg("Error auto-setting lineup");
        showToast && showToast("Error auto-setting lineup", "error");
        setTimeout(() => setSaveMsg(""), 2000);
      });
  };

  // Save team name
  const handleSaveTeamSettings = async () => {
    try {
      validateAllowedText(teamName, { fieldName: "Team display name", maxLength: 100 });
      await teamManagementApi.saveTeamCustomization(leagueId, selectedTeam, teamName, null, currentUser);
      // Update the team names state to reflect the change
      setTeamNames(prev => ({
        ...prev,
        [selectedTeam]: teamName
      }));
      setSaveMsg("Team settings saved!");
      showToast && showToast("Team settings saved!", "success");
      setTimeout(() => setSaveMsg(""), 2000);
    } catch (error) {
      console.error('Error saving team settings:', error);
      const msg = error.message || "Error saving team settings";
      setSaveMsg(msg);
      showToast && showToast(msg, "error");
      setTimeout(() => setSaveMsg(""), 2000);
    }
  };

  // --- Roster summary logic ---
  // Count starters by position
  const starterCounts = { QB: 0, RB: 0, WR: 0, TE: 0, FLEX: 0, FLEX_SILVER: 0, FLEX_GOLD: 0, K: 0 };
  getStarterSlotDefinitions(leagueSettings || {}).forEach(slot => {
    const pid = lineup[week][slot.key]?.player_id;
    if (pid) {
      if (slot.key === 'FLEX') starterCounts.FLEX++;
      else if (slot.key === 'FLEX_SILVER') starterCounts.FLEX_SILVER++;
      else if (slot.key === 'FLEX_GOLD') starterCounts.FLEX_GOLD++;
      else if (slot.key.startsWith('K')) starterCounts.K++;
      else starterCounts[slot.key.replace(/[0-9]/g, '')]++;
    }
  });
  // Count bench by position
  const benchCounts = { QB: 0, RB: 0, WR: 0, TE: 0 };
  roster.filter(p => !getStarterSlotDefinitions(leagueSettings || {}).some(slot => lineup[week][slot.key]?.player_id === p.player_id)).forEach(p => {
    if (benchCounts[p.position] !== undefined) benchCounts[p.position]++;
  });


  return (
    <div className="ffb-bg">
      <header className="ffb-header">
        <h1 className="ffb-header-title">🏈 Team Management 🏈</h1>
      </header>

      <div style={{ padding: 32, minHeight: 600, maxWidth: 900, margin: "0 auto" }}>

        {/* Navigation buttons */}
        <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
          <button
            onClick={onBack}
            className="ffb-btn"
            aria-label="Back"
          >
            Back to Home
          </button>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ color: "#2563eb", margin: 0 }}>Team Management</h2>
          <div style={{ display: "flex", gap: 12 }}>
            <button
              className="ffb-btn"
              onClick={() => navigate(`/league/${leagueId}/trades`)}
            >
              Trades
            </button>
          </div>
        </div>
        {incomingTradeCount > 0 && (
          <div
            style={{
              background: "#fef9c3",
              border: "1px solid #facc15",
              borderRadius: 10,
              padding: "14px 18px",
              marginBottom: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <span style={{ color: "#854d0e", fontWeight: 600 }}>
              You have {incomingTradeCount} pending trade
              {incomingTradeCount === 1 ? "" : "s"} waiting for your response.
            </span>
            <button
              className="ffb-btn"
              onClick={() => navigate(`/league/${leagueId}/trades`)}
            >
              Review trades
            </button>
          </div>
        )}
        <div
          style={{
            background: isMyTeam ? "#e0f2fe" : "#f1f5f9",
            padding: 16,
            borderRadius: 8,
            marginBottom: 20,
            border: isMyTeam ? "2px solid #0288d1" : "1px solid #e2e8f0",
          }}
        >
          <h3 style={{ margin: "0 0 12px 0", color: "#334155" }}>
            {isMyTeam ? "🏈 " : ""}Team {teamName}
            {isMyTeam && <span style={{ color: "#0288d1", fontSize: "0.9em", marginLeft: 8 }}>(Your Team)</span>}
            {!isMyTeam && !isCommissioner && <span style={{ color: "#64748b", fontSize: "0.9em", marginLeft: 8 }}>(View Only)</span>}
            {!isMyTeam && isCommissioner && <span style={{ color: "#059669", fontSize: "0.9em", marginLeft: 8 }}>(Commissioner Edit)</span>}
          </h3>
          <div style={{ color: "#334155" }}>
            <strong>Record:</strong> 0-0 | <strong>Points For:</strong> 0 | <strong>Points Against:</strong> 0
          </div>
          <span style={{ color: "#334155" }}>
            <strong>Current Rank:</strong> 1st
          </span>
          {!canEdit && (
            <div style={{ 
              marginTop: 8, 
              padding: 8, 
              background: "#fef3c7", 
              borderRadius: 4, 
              color: "#92400e",
              fontSize: "0.9em"
            }}>
              ⚠️ You can only view this team's settings. Only commissioners can edit other teams.
            </div>
          )}
        </div>
        
        {canEdit && (
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              padding: 16,
              marginBottom: 20,
            }}
          >
            <h3 style={{ margin: "0 0 8px", color: "#2563eb", fontSize: 16 }}>Drop player to free agency</h3>
            <p style={{ margin: "0 0 12px", fontSize: 13, color: "#64748b" }}>
              Opens roster space for unequal trades or waiver adds. Only available when rosters are unlocked ({getLockStatusMessage(unlockDay, unlockHour)}).
            </p>
            {dropMsg && (
              <p style={{ margin: "0 0 10px", fontSize: 13, color: dropMsg.startsWith("✅") ? "#166534" : "#dc2626" }}>
                {dropMsg}
              </p>
            )}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <select
                value={dropPlayerId}
                onChange={(e) => setDropPlayerId(e.target.value)}
                className="ffb-select"
                disabled={lockStatus.isLocked}
              >
                <option value="">-- Select player to drop --</option>
                {roster.map((p) => (
                  <option key={p.player_id} value={p.player_id}>
                    {p.player_name} ({p.position})
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={`ffb-btn ${lockStatus.isLocked || !dropPlayerId ? "ffb-btn-disabled" : ""}`}
                disabled={lockStatus.isLocked || !dropPlayerId}
                onClick={handleDropToWaivers}
              >
                {lockStatus.isLocked ? "Rosters locked" : "Drop player"}
              </button>
            </div>
          </div>
        )}

        {/* Lineup editing info */}
        {weekLineupLocked ? (
          <div style={{
            marginBottom: 24,
            padding: 12,
            borderRadius: 8,
            background: "#fef2f2",
            border: "2px solid #fecaca",
            color: "#991b1b",
            fontWeight: 600,
            textAlign: "center",
          }}>
            Week {week} scoring is final — lineups can no longer be changed.
          </div>
        ) : (
          <>
            <div style={{
              marginBottom: lineupHasUnsavedChanges ? 12 : 24,
              padding: 12,
              borderRadius: 8,
              background: "#f0fdf4",
              border: "2px solid #bbf7d0",
              color: "#166534",
              fontSize: 14,
              textAlign: "center",
              lineHeight: 1.5,
            }}>
              {getLineupAndTransactionInfoCopy(resolveUnlockSettings(leagueSettings))}
            </div>
            {lineupHasUnsavedChanges && (
              <div style={{
                marginBottom: 24,
                padding: 12,
                borderRadius: 8,
                background: "#fffbeb",
                border: "2px solid #fcd34d",
                color: "#92400e",
                fontWeight: 600,
                fontSize: 14,
                textAlign: "center",
                lineHeight: 1.5,
              }}>
                You have unsaved lineup changes for Week {week}. Save your lineup to lock in starters before kickoff.
              </div>
            )}
          </>
        )}
        
        <div style={{ marginBottom: 24, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          {/* Team Customization */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label htmlFor="teamNameInput" style={{ fontWeight: 600, color: canEdit ? "#2563eb" : "#64748b" }}>Team Name:</label>
            <input
              type="text"
              id="teamNameInput"
              value={teamName}
              onChange={e => setTeamName(e.target.value)}
              disabled={!canEdit}
              style={{ 
                fontSize: 18, 
                fontWeight: 600, 
                border: "1px solid #cbd5e1", 
                borderRadius: 4, 
                padding: 8, 
                width: 180, 
                background: canEdit ? "#2563eb" : "#f1f5f9", 
                color: canEdit ? "#fff" : "#64748b",
                opacity: canEdit ? 1 : 0.6
              }}
            />
            <button 
              onClick={handleSaveTeamSettings} 
              disabled={!canEdit}
              className={`ffb-btn ${!canEdit ? "ffb-btn-disabled" : ""}`} 
              style={{ marginLeft: 8 }}
            >
              Save Team Name
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 600, color: canEdit ? "#2563eb" : "#64748b" }}>Team Icon:</span>
            <TeamIconSelector 
              teamName={selectedTeam}
              onIconChange={() => {
                // Icon change handled by TeamIconSelector component
              }}
              currentUser={currentUser}
              leagueId={leagueId}
              disabled={!canEdit}
            />
          </div>
          <label htmlFor="teamSelect" style={{ fontWeight: 600, color: "#2563eb" }}>Team:</label>
          <select
            id="teamSelect"
            value={selectedTeam}
            onChange={e => setSelectedTeam(e.target.value)}
            className="ffb-select"
            style={{ minWidth: "200px" }}
          >
            {Array.from({ length: teamCount }, (_, i) => `Team${i + 1}`).map((name) => {
              const isUserTeam = userTeamInfo?.user_team === name;
              const displayName = teamNames[name] || name.replace("Team", "Team ");
              const label = isUserTeam ? `${displayName} (Your Team)` : displayName;
              return (
                <option key={name} value={name}>{label}</option>
              );
            })}
          </select>
          <label htmlFor="weekSelect" style={{ fontWeight: 600, color: "#2563eb" }}>Week:</label>
          <select
            id="weekSelect"
            value={week}
            onChange={e => setWeek(Number(e.target.value))}
            className="ffb-select"
          >
            {Array.from({ length: totalWeeks }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                Week {i + 1}
                {i + 1 === calendarWeek ? " (current)" : ""}
              </option>
            ))}
          </select>
          <button
            onClick={handleSaveLineup}
            disabled={weekLineupLocked || !canEdit}
            className="ffb-btn"
            aria-label="Save Lineup"
          >
            {weekLineupLocked ? "Week Final" : "Save Lineup"}
          </button>
          {week > 1 && (
            <button
              type="button"
              onClick={handleCopyLastWeek}
              disabled={weekLineupLocked || !canEdit || copyingLastWeek}
              className="ffb-btn"
              aria-label="Copy last week lineup"
              title="Copy Week N-1 starters and bench into this week (does not save until you click Save Lineup)"
            >
              {copyingLastWeek ? "Copying…" : `Copy Week ${week - 1} Lineup`}
            </button>
          )}
          <button
            onClick={handleAutoSetRoster}
            disabled={weekLineupLocked || !canEdit}
            className="ffb-btn"
            aria-label="Set Roster Automatically"
            title="Fill starters and bench from your roster and save immediately (not a preview)"
          >
            {weekLineupLocked ? "Week Final" : "Set Roster Automatically"}
          </button>
          {saveMsg && <span style={{ color: "#22c55e", fontWeight: 600, marginLeft: 12 }}>{saveMsg}</span>}
        </div>
        <div style={{ display: "flex", gap: 32, alignItems: "flex-start" }}>
          {/* Starters (single column) */}
          <div style={{ flex: 1.5, minWidth: 260, background: "#f8fafc", borderRadius: 8, boxShadow: "0 2px 8px #e0e7ff", padding: 16 }}>
            {isCommissioner && !isMyTeam && (
              <div style={{ color: '#dc2626', fontWeight: 600, marginBottom: 10, textAlign: 'center' }}>
                Commissioner editing another team's roster
              </div>
            )}
            <h3 style={{ color: "#2563eb", marginTop: 0, marginBottom: 8, textAlign: "center" }}>Starters</h3>
            <div style={{ color: '#64748b', fontSize: 14, marginBottom: 12, textAlign: 'center' }}>
              Set your starting lineup for this week. Starters will score points; bench players will not. Save to lock in starters — scores reveal at kickoff (day and time stay hidden until then).
            </div>
            <div style={{ color: '#334155', fontSize: 13, marginBottom: 10, textAlign: 'center' }}>
              <b>Roster Summary:</b> {getStarterSlotDefinitions(leagueSettings || {}).length} Starters, {BENCH_COUNT} Bench<br/>
              Starters: {starterCounts.QB} QB, {starterCounts.RB} RB, {starterCounts.WR} WR, {starterCounts.TE} TE, {starterCounts.FLEX} FLEX{(starterCounts.FLEX_SILVER ? `, ${starterCounts.FLEX_SILVER} Silver` : "")}{(starterCounts.FLEX_GOLD ? `, ${starterCounts.FLEX_GOLD} Gold` : "")}{(leagueSettings?.kCount > 0 ? `, ${starterCounts.K} K` : "")}<br/>
              Bench: {benchCounts.QB} QB, {benchCounts.RB} RB, {benchCounts.WR} WR, {benchCounts.TE} TE
            </div>
            <table className="ffb-starter-lineup-table">
              <tbody>
                {getStarterSlotDefinitions(leagueSettings || {}).map((slot) => {
                  const currentId = lineup[week][slot.key]?.player_id;
                  return (
                    <tr key={slot.key} style={starterSlotRowStyle(slot.rowTint)}>
                      <td style={{ fontWeight: 600, color: "#334155", minWidth: 60 }}>{slot.label}:</td>
                      <td>
                        <select
                          id={`starterSelect-${slot.key}`}
                          value={currentId}
                          onChange={e => handleAssignStarter(slot.key, e.target.value)}
                          disabled={!isStarterSlotEditable(slot.key)}
                          className="ffb-select"
                          title={
                            revealSchedule[slot.key]?.revealed
                              ? "This slot's score has already been revealed."
                              : "Select a player for this starting slot. Only eligible positions are shown."
                          }
                        >
                          <option value="">-- Select --</option>
                          {getOptions(slot.eligible, currentId).map((p) => (
                            <option key={p.player_id} value={p.player_id}>
                              {p.player_name} ({p.position})
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ fontSize: 13, color: "#64748b", minWidth: 140 }}>
                        {(() => {
                          if (!lineup[week][slot.key]?.player_id) return "—";
                          const sched = revealSchedule[slot.key];
                          if (!sched?.assigned) {
                            return "Save lineup to lock in starter";
                          }
                          if (sched.revealed) {
                            return sched.reveal_at_label
                              ? `Revealed (${sched.reveal_at_label})`
                              : "Score revealed";
                          }
                          return "Score pending — reveals at kickoff";
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Bench */}
          <div
            className="bench-section"
            style={{
              flex: 1,
              minWidth: 180,
              background: "#f1f5f9",
              borderRadius: 8,
              boxShadow: "0 2px 8px #e0e7ff",
              padding: 16,
              color: "#334155",
            }}
          >
            <h4 style={{ color: "#64748b", marginTop: 0, marginBottom: 8 }}>Bench</h4>
            <div style={{ color: '#64748b', fontSize: 14, marginBottom: 12 }}>
              Bench players do not score points. Use the dropdowns to move players between bench and starters.
            </div>
            {/* Show all roster players not in starting slots as bench */}
            {roster.filter(p => !getStarterSlotDefinitions(leagueSettings || {}).some(slot => lineup[week][slot.key]?.player_id === p.player_id)).map((p, idx) => (
              <div key={p.player_id} style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
                <label
                  htmlFor={`benchSlotLabel-${p.player_id}`}
                  style={{
                    fontWeight: 600,
                    color: "#334155",
                    minWidth: 60,
                  }}
                  title="Bench slot. Players here will not score points this week."
                >
                  Slot {idx + 1}:
                </label>
                <span style={{ color: "#334155" }}>
                  {p.player_name} ({p.position})
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <footer
        style={{
          textAlign: "center",
          color: "#94a3b8",
          fontSize: "0.98em",
          marginTop: 40,
          padding: 20,
        }}
      >
        
      </footer>
    </div>
  );
} 